// The change over stage runs in this browser (its GPU): the live preview and
// drawing the files. It is loaded into a same-origin frame (srcdoc) whose
// <base> points at the API's stage files, so no server header can block it.
// Drawing: claim the current version → draw it in a hidden frame (colour +
// alpha, WebCodecs) → upload → the server makes ProRes / WebM / MP4.
import { useEffect, useState } from 'react';
import api from '../utils/api';
import { changeoverService } from '../services/apiService';

const stageBase = () => new URL(`${api.defaults.baseURL || '/api'}/changeovers/live/`, window.location.href).href;

/** The stage page, for <iframe srcDoc>. */
export const stageDoc = () => `<!doctype html><html><head><meta charset="utf-8"><base href="${stageBase()}">
<style>html,body{margin:0;height:100%;background:#000;overflow:hidden}</style></head><body>
<script type="importmap">{"imports":{"three":"${stageBase()}three/build/three.module.js","three/addons/":"${stageBase()}three/addons/"}}</script>
<script type="module" src="${stageBase()}stage.mjs"></script></body></html>`;

export const canDraw = () => typeof window.VideoEncoder !== 'undefined';

// ─── progress, shared by the gallery and the studio ────────────────────
const state = new Map();                // id → { phase: 'waiting'|'drawing'|'uploading'|'packaging', p, error }
const listeners = new Set();
const set = (id, v) => {
  if (v) state.set(id, v); else state.delete(id);
  listeners.forEach((l) => l());
};
/** This browser's work on change over `id` (null when none). */
export function useDrawState(id) {
  const [, tick] = useState(0);
  useEffect(() => {
    const l = () => tick((n) => n + 1);
    listeners.add(l);
    return () => listeners.delete(l);
  }, []);
  return state.get(id) || null;
}

// ─── the hidden drawing frame ──────────────────────────────────────────
let frame = null;
let ready = null;
function drawingFrame() {
  if (ready) return ready;
  frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.title = 'changeover-drawing';
  // Off screen but rendered (a display:none frame may not draw).
  Object.assign(frame.style, { position: 'fixed', left: '-4000px', top: '0', width: '960px', height: '540px', border: '0', opacity: '0', pointerEvents: 'none' });
  frame.srcdoc = stageDoc();
  document.body.appendChild(frame);
  ready = new Promise((resolve, reject) => {
    const timer = setInterval(() => frame.contentWindow?.postMessage({ type: 'changeover:hello' }, '*'), 300);
    const fail = setTimeout(() => { clearInterval(timer); window.removeEventListener('message', on); ready = null; frame.remove(); reject(new Error('The drawing engine did not start.')); }, 45000);
    function on(e) {
      if (e.source !== frame.contentWindow || e.data?.type !== 'changeover:ready') return;
      clearInterval(timer); clearTimeout(fail);
      window.removeEventListener('message', on);
      resolve(frame);
    }
    window.addEventListener('message', on);
  });
  return ready;
}

function exportIn(f, spec, options, onProgress) {
  const id = Math.random().toString(36).slice(2);
  return new Promise((resolve, reject) => {
    function on(e) {
      if (e.source !== f.contentWindow || e.data?.id !== id) return;
      const m = e.data;
      if (m.type === 'changeover:exportProgress') onProgress(m.p);
      else {
        window.removeEventListener('message', on);
        if (m.type === 'changeover:exported') resolve(m); else reject(new Error(m.message));
      }
    }
    window.addEventListener('message', on);
    f.contentWindow.postMessage({ type: 'changeover:export', id, spec, options }, '*');
  });
}

let options = null;
let queue = Promise.resolve();
const queued = new Set();

/**
 * Draw change over `id` in this browser (one at a time), unless another
 * browser is already drawing it. Resolves with the record when its files are
 * made (or null when it was not ours to draw).
 */
export function drawChangeover(id) {
  if (queued.has(id)) return queue;
  queued.add(id);
  set(id, { phase: 'waiting', p: 0 });
  const job = queue.then(async () => {
    let claim;
    let renew;
    try {
      if (!canDraw()) throw new Error('This browser cannot draw change overs — use Chrome or Edge (on https).');
      claim = await changeoverService.claimDraw(id);
      if (!claim.granted) { set(id, null); return null; }
      options = options || (await changeoverService.options()).export;
      // Keep the claim while drawing.
      renew = setInterval(() => changeoverService.claimDraw(id, claim.token).catch(() => {}), 12000);
      const f = await drawingFrame();
      const t0 = performance.now();
      set(id, { phase: 'drawing', p: 0 });
      const out = await exportIn(f, claim.spec, { fps: options.fps, lead: options.lead, footage: 'footage/0.mp4', footageOffset: options.footageOffset, sub: 8 }, (p) => set(id, { phase: 'drawing', p }));
      const drawMs = Math.round(performance.now() - t0);
      set(id, { phase: 'uploading', p: 0 });
      const rec = await changeoverService.uploadDrawing(id, {
        token: claim.token, version: claim.version, timeline: out.timeline, introFrames: out.introFrames, drawMs,
        colour: out.colour, alpha: out.alpha, intro: out.intro, poster: out.poster,
      }, (e) => {
        const p = e.total ? e.loaded / e.total : 0;
        set(id, p >= 1 ? { phase: 'packaging', p: 1 } : { phase: 'uploading', p });
      });
      set(id, null);
      return rec;
    } catch (e) {
      set(id, { phase: 'error', p: 0, error: e?.response?.data?.message || e.message });
      return null;
    } finally {
      clearInterval(renew);
      queued.delete(id);
    }
  });
  queue = job.catch(() => null);
  return job;
}
